import { apiService } from '@/services/ApiService.ts';
import { useSessionStore } from '@/stores/SessionStore.ts';
import { SubscriptionTier } from '@/types/SubscriptionTier.ts';
import { createTextToast } from '@/utils/ToastUtils.ts';
import { AuthenticationService } from '@/services/AuthenticationService.ts';
import { useUserInfoStore } from '@/stores/UserInfoStore.ts';
import { useSettingsStore } from '@/stores/SettingsStore.ts';
import { useAuthStore } from '@/stores/AuthStore.ts';
import { wsAuthLogIn } from '@/ws/requests/WsAuthLogIn.ts';
import { fetchAiModelsAction } from '@/actions/app/FetchAiModelsAction.ts';
import { router, ROUTES } from '@/router.ts';
import { requestInputSourcesAction } from '@/actions/electron/RequestInputSourcesAction.ts';

/**
 * @throws {Error} On API error response
 */
export const logInAction = async (username: string, password: string) => {
    const apiResponse = await apiService.getApi().authLoginV1({
        loginRequestDto: {
            username,
            password,
        },
    });

    const { creditsRemaining, username: userUsername, subscriptionTier } = apiResponse.userData;

    // Web view trial check
    const sessionStore = useSessionStore();
    sessionStore.isRunBlocked = false;

    if (sessionStore.isInsideWebView && subscriptionTier === SubscriptionTier.Trial) {
        createTextToast(
            'Web view is not available to trial users.',
            'Please upgrade your account to continue.',
            'destructive',
            false,
        );

        return;
    }

    const { refreshToken, accessTokenExpiresIn, fingerprintHash, accessToken } =
        apiResponse.tokenData;

    AuthenticationService.instance.storeTokenData(
        accessToken,
        refreshToken,
        fingerprintHash,
        accessTokenExpiresIn,
    );

    const authStore = useAuthStore();
    authStore.isLoggedIn = true;

    const userInfoStore = useUserInfoStore();
    userInfoStore.username = userUsername;
    userInfoStore.creditsRemaining = creditsRemaining;
    userInfoStore.subscriptionTier = subscriptionTier as SubscriptionTier;

    const settingsStore = useSettingsStore();
    settingsStore.rememberedUsername = userUsername;

    wsAuthLogIn(accessToken, !sessionStore.isInsideWebView);

    await fetchAiModelsAction();
    void requestInputSourcesAction();

    // Restart onboarding if necessary
    if (settingsStore.isOnboarding) {
        await router.push({ name: ROUTES.onboarding });
    } else {
        await router.push({ name: ROUTES.index });
    }
};
